import type { DrillType } from "@-/dev.improve.haniverse.lib.drill.entities";
import type { MorphemeHrefObject } from "@-/dev.improve.haniverse.lib.morpheme.href.entities";
import type { ReactNode } from "react";
import { isWordDrillType } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._context";
import { GraphemeDrillGateSuspense } from "#grapheme-drill-gate-suspense";
import { WordDrillGateSuspense } from "#word-drill-gate-suspense";

/**
 * A drill card's data: exactly one branch gate for the card's kind. Each gate runs ONLY its own
 * queries (so no inert-sentinel query keeps hook order stable across kinds), renders its own
 * not-found states, then provides the shared `DrillPageContext` plus its branch context around the
 * children written for that branch.
 */
export function DrillPageSuspense({
  character,
  contextSentence,
  drillType,
  grapheme,
  morphemeHrefObject,
  word,
}: {
  character: string;
  /** The sentence chosen during queue preparation, preferred by a word drill when still available. */
  contextSentence?: string;
  drillType: DrillType;
  /** The card's content when it drills a grapheme (meaning / pronunciation). */
  grapheme: ReactNode;
  morphemeHrefObject: MorphemeHrefObject;
  /** The card's content when it drills a word (`word-sentence` / sentence production). */
  word: ReactNode;
}) {
  return isWordDrillType(drillType) ? (
    <WordDrillGateSuspense
      character={character}
      contextSentence={contextSentence}
      drillType={drillType}
      morphemeHrefObject={morphemeHrefObject}
    >
      {word}
    </WordDrillGateSuspense>
  ) : (
    <GraphemeDrillGateSuspense
      character={character}
      drillType={drillType}
      morphemeHrefObject={morphemeHrefObject}
    >
      {grapheme}
    </GraphemeDrillGateSuspense>
  );
}
